import {
  Menu,
  MenuButton,
  MenuHeading,
  MenuItem,
  MenuItems,
  MenuSection,
  MenuSeparator
} from '@headlessui/react'
import {
  BookmarkIcon,
  BriefcaseIcon,
  ChartBarIcon,
  LinkIcon,
  PaperClipIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline'
import { format } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { BeatLoader } from 'react-spinners'

import PdfBundlePreview from '../../assets/images/pdf-bundle-preview.svg'
import MatchingProfilePreview from '../../assets/images/strengthprofile-preview.png'
import StrengthProfilePreview from '../../assets/images/strengthprofile-preview1.png'
import { colors } from '../../constants/colors'
import {
  MATCHINGPROFILE,
  TALENTPROFILE
} from '../../constants/evaluation-types'
import trackEvents from '../../constants/track-events'
import {
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { query } from '../../graphql'
import { getInviteToAssessmentLink, notifyBugsnag } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { getAvailableProfileTypes } from '../../helpers/pdf-bundle'
import { openPdfLoadingWindow } from '../../helpers/pdf-loading'
import { isFixtureSampleSpaceId } from '../../helpers/sample-data'
import { getPrimaryTalentStatus } from '../../helpers/spaces-table-data'
import { getTalentStatusText } from '../../helpers/talent-statuses'
import { useCopyToClipboard } from '../../hooks/use-copy-to-clipboard'
import ProfileFeedbackModal from '../feedback/profile-feedback-modal'
import { AnimatedCopyIcon } from '../motion'
import State from '../state'
import Button from '../tailwind/Button'
import DetailField from '../tailwind/DetailField'
import GroupLabel from '../tailwind/GroupLabel'
import Panel from '../tailwind/Panel'
import Progress from '../tailwind/Progress'
import ScorePill from '../tailwind/ScorePill'
import SectionHeading from '../tailwind/SectionHeading'
import SpaceExpiresAtField from './space-expires-at-field'

const SpaceDetailsAssessment = ({
  space,
  refetchSpace,
  career,
  spaceActions,
  canExportPdfBundle,
  isPdfBundleLoading,
  onOpenPdfBundle
}) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { language } = useLanguageContext()
  const { success, error } = useNotificationContext()
  const {
    activeSpace: {
      hasSeenOrSubmittedMatchingProfileFeedback,
      hasSeenOrSubmittedTalentProfileFeedback
    }
  } = useUserContext()

  const navigate = useNavigate()

  const {
    id: space_id,
    progress,
    owner,
    score,
    status,
    language: space_language
  } = space

  const { assessment_status } = status
  const { isV1ScoreVisible, isV2ScoreVisible } = partner

  const {
    id: career_id,
    title: c_title,
    external_custom_id,
    archived,
    diagnostic_version
  } = career || {}

  const career_title_with_archived = archived
    ? t('talents.career_archived_title', { career_title: c_title })
    : c_title

  const career_title = external_custom_id
    ? `${career_title_with_archived} | ${external_custom_id}`
    : career_title_with_archived

  const { setDisplaySetSpaceState } = spaceActions

  const inviteLinkCopy = useCopyToClipboard({ resetAfter: 1000 * 3 })
  const [displayFeedbackModal, setDisplayFeedbackModal] = useState(false)
  const [feedbackModalContext, setFeedbackModalContext] = useState(null)

  const inviteLink = getInviteToAssessmentLink({
    space_id,
    language: space_language
  })

  const copyInviteLinkToClipboard = async () => {
    if (!(await inviteLinkCopy.copy(inviteLink))) {
      error(t('global.copy_error'))
    }
  }

  const displayScore =
    (diagnostic_version === 1 && isV1ScoreVisible) ||
    (diagnostic_version === 2 && isV2ScoreVisible)

  // Talent-Status (vom Team gesetzt) und Assessment-Status (vom System) sind
  // zwei Achsen und werden im Hero getrennt gezeigt.
  const { status: talentStatus, isUserStatus } = getPrimaryTalentStatus(space)
  const talentStatusText = getTalentStatusText({
    status: talentStatus,
    t,
    language,
    progress
  })
  const assessmentStatusText = assessment_status?.status?.label
    ? t(assessment_status.status.label, { progress })
    : null
  const talentStatusUpdatedAt = space.talent_status_meta?.updated_at
    ? format(new Date(space.talent_status_meta.updated_at), 'd. MMM yyyy', {
        locale: { de, en }[language]
      })
    : null

  // Dieselbe Regel entscheidet, was der PDF-Dialog anbietet.
  const availableProfileTypes = getAvailableProfileTypes({
    progress,
    careerId: career_id
  })

  const attachments = [
    {
      key: MATCHINGPROFILE,
      available: availableProfileTypes.includes(MATCHINGPROFILE),
      filename: t('pages.talent.matching_profile'),
      background: '#2C46B2',
      imageSrc: MatchingProfilePreview,
      dropdownSections: [
        {
          items: [
            {
              key: MATCHINGPROFILE,
              label: 'Deutsch',
              variables: {
                career_id,
                user_id: owner,
                space_id,
                type: diagnostic_version === 2 ? MATCHINGPROFILE : undefined,
                language: 'de'
              },
              eventName: trackEvents.VIEW_MATCHING_PROFILE,
              eventParams: { diagnostic_version },
              displayProfileFeedback:
                diagnostic_version === 2 &&
                !hasSeenOrSubmittedMatchingProfileFeedback
            },
            {
              key: MATCHINGPROFILE,
              label: 'English',
              variables: {
                career_id,
                user_id: owner,
                space_id,
                type: diagnostic_version === 2 ? MATCHINGPROFILE : undefined,
                language: 'en'
              },
              eventName: trackEvents.VIEW_MATCHING_PROFILE,
              eventParams: { diagnostic_version },
              displayProfileFeedback:
                diagnostic_version === 2 &&
                !hasSeenOrSubmittedMatchingProfileFeedback
            }
          ]
        }
      ]
    },
    {
      key: TALENTPROFILE,
      available: availableProfileTypes.includes(TALENTPROFILE),
      filename: t('pages.talent.strength_profile_v2'),
      background: '#fe79a7',
      imageSrc: StrengthProfilePreview,
      displayProfileFeedback: !hasSeenOrSubmittedTalentProfileFeedback,
      dropdownSections: [
        {
          heading: t('pages.talent.attachment_section_current'),
          items: [
            {
              key: TALENTPROFILE,
              label: 'Deutsch',
              variables: {
                user_id: owner,
                language: 'de',
                type: TALENTPROFILE
              },
              eventName: trackEvents.VIEW_STRENGTH_PROFILE_V2,
              displayProfileFeedback: !hasSeenOrSubmittedTalentProfileFeedback
            },
            {
              key: TALENTPROFILE,
              label: 'English',
              variables: {
                user_id: owner,
                language: 'en',
                type: TALENTPROFILE
              },
              eventName: trackEvents.VIEW_STRENGTH_PROFILE_V2,
              displayProfileFeedback: !hasSeenOrSubmittedTalentProfileFeedback
            },
            {
              key: TALENTPROFILE,
              label: 'Nederlands',
              comingSoon: true,
              variables: {
                user_id: owner,
                language: 'nl',
                type: TALENTPROFILE
              },
              eventName: trackEvents.VIEW_STRENGTH_PROFILE_V2,
              displayProfileFeedback: !hasSeenOrSubmittedTalentProfileFeedback
            }
          ]
        },
        // Fixture-Semantik: Beispieltalente zeigen Scores aus globalen
        // Fixtures, klassische PDFs gibt es für sie nicht
        ...(!isFixtureSampleSpaceId(space_id)
          ? [
              {
                heading: t('pages.talent.attachment_section_classic'),
                items: [
                  {
                    key: TALENTPROFILE,
                    label: 'Deutsch',
                    variables: {
                      user_id: owner,
                      language: 'de',
                      type: TALENTPROFILE,
                      show_old_ui: true
                    },
                    eventName: trackEvents.VIEW_STRENGTH_PROFILE_V1,
                    displayProfileFeedback: false
                  },
                  {
                    key: TALENTPROFILE,
                    label: 'English',
                    variables: {
                      user_id: owner,
                      language: 'en',
                      type: TALENTPROFILE,
                      show_old_ui: true
                    },
                    eventName: trackEvents.VIEW_STRENGTH_PROFILE_V1,
                    displayProfileFeedback: false
                  }
                ]
              }
            ]
          : [])
      ]
    }
  ].filter(({ available }) => available)

  const generatePdf = async ({
    key,
    variables,
    eventName,
    eventParams,
    displayProfileFeedback
  }) => {
    if (displayProfileFeedback) {
      setFeedbackModalContext(key)
      setDisplayFeedbackModal(true)
    }

    const pdfDocument = openPdfLoadingWindow({ t, language })

    try {
      const pdf = await query({ query: 'generatePdf', variables })
      pdfDocument.location.href = JSON.parse(pdf).headers.Location
      success(t('pdf_success'))
      trackEvent(eventName, eventParams)
    } catch (err) {
      error()
      notifyBugsnag(err)
      pdfDocument.close()
    }
  }

  return (
    <>
      <Panel>
        <section aria-labelledby='assessment-information-title'>
          <SectionHeading
            id='assessment-information-title'
            title={t('pages.talent.space_details_assessment_header')}
            description={t('pages.talent.space_details_assessment_description')}
          />

          <div className='space-y-6 border-t border-gray-300 pt-5'>
            {/* Status-Hero: links der Talent-Status mit klarer Aktion, rechts
                der Assessment-Stand. Der Status ist jederzeit setzbar. */}
            <div
              id='talent-status-hero'
              className='flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 lg:flex-row lg:items-center lg:justify-between'
            >
              <div>
                <GroupLabel as='p'>
                  {t('pages.talent.status_eyebrow')}
                </GroupLabel>
                <div className='mt-2 flex flex-wrap items-center gap-x-3 gap-y-2'>
                  {isUserStatus ? (
                    <>
                      <State
                        text={talentStatusText}
                        color={talentStatus.color}
                        height={28}
                      />
                      <Button.SecondaryBase
                        text={t('pages.talent.status_change_action')}
                        icon={<PencilSquareIcon className='size-4' />}
                        onClick={() => setDisplaySetSpaceState(true)}
                      />
                      {talentStatusUpdatedAt && (
                        <span className='basis-full text-xs text-gray-500'>
                          {t('pages.talent.status_updated_at', {
                            date: talentStatusUpdatedAt
                          })}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <Button.PrimaryBase
                        text={t('pages.talent.status_set_action')}
                        icon={<BookmarkIcon className='size-4' />}
                        onClick={() => setDisplaySetSpaceState(true)}
                      />
                      <span className='text-sm text-gray-500'>
                        {t('pages.talent.status_empty')}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className='border-t border-gray-100 pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0'>
                <GroupLabel as='p'>
                  {t('pages.talent.assessment_eyebrow')}
                </GroupLabel>
                <div className='mt-2 flex flex-wrap items-center gap-3'>
                  {assessmentStatusText && (
                    <State
                      text={assessmentStatusText}
                      color={assessment_status.status.color}
                      height={28}
                    />
                  )}
                  {progress > 0 ? (
                    <div className='flex items-center gap-3'>
                      <Progress progress={progress} />
                      <span className='text-sm font-semibold text-gray-700'>
                        {progress}%
                      </span>
                    </div>
                  ) : (
                    <span className='text-sm text-gray-400'>
                      {t('pages.talent.not_started')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <dl className='grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2'>
              {!!career_id && (
                <DetailField
                  icon={BriefcaseIcon}
                  label={t('pages.talent.space_details_career_label')}
                  value={career_title}
                  onClick={() => navigate(`/career/${career_id}`)}
                />
              )}

              {progress === 100 && career_id && (
                <DetailField
                  icon={ChartBarIcon}
                  label={t('pages.talent.space_details_score_label')}
                >
                  <ScorePill
                    score={displayScore ? score : null}
                    emptyLabel={t('pages.talent.space_details_score_hidden')}
                  />
                </DetailField>
              )}

              {/* Ablauf mit Datepicker, bei archivierter Stelle gesperrt (D16) */}
              <SpaceExpiresAtField space={space} refetchSpace={refetchSpace} />

              <DetailField
                icon={LinkIcon}
                label={t('pages.talent.space_details_via_link_label')}
                className='sm:col-span-2'
              >
                <button
                  type='button'
                  onClick={copyInviteLinkToClipboard}
                  className='group/link flex w-full max-w-xl items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-left transition hover:border-gray-300'
                >
                  <span className='font-mono truncate text-xs text-gray-600'>
                    {inviteLink}
                  </span>
                  <AnimatedCopyIcon
                    copied={inviteLinkCopy.copied}
                    className='ml-auto size-4 text-gray-400 group-hover/link:text-gray-600'
                  />
                </button>
              </DetailField>
            </dl>

            {!!attachments.length && (
              <div>
                <GroupLabel>{t('pages.talent.attachments')}</GroupLabel>
                <div className='mt-1 text-sm font-medium text-gray-900'>
                  <ul role='list' className='flex flex-row flex-wrap'>
                    {attachments.map((attachment, index) => (
                      <Menu
                        as='li'
                        key={index}
                        className='relative mr-4 mt-2 list-none'
                      >
                        <MenuButton
                          as='div'
                          style={{
                            width: 172,
                            height: 243,
                            background: attachment.background
                          }}
                          className='relative flex cursor-pointer flex-col items-center rounded-xl px-6 hover:opacity-70'
                        >
                          <div
                            style={{ height: '60%' }}
                            className='flex w-full items-center justify-center'
                          >
                            <img
                              style={{ maxWidth: 100 }}
                              src={attachment.imageSrc}
                            />
                          </div>

                          <div className='absolute bottom-6 flex flex-col items-center'>
                            <span className='mb-2 text-lg font-medium text-white'>
                              {attachment.filename}
                            </span>
                            <Button.SecondaryBase
                              text={t('pages.talent.open_action')}
                            />
                          </div>

                          <PaperClipIcon
                            className='absolute right-2 top-2 size-5 flex-shrink-0 text-white'
                            aria-hidden='true'
                          />
                        </MenuButton>
                        <MenuItems
                          anchor={{ to: 'bottom start', gap: 4 }}
                          className='z-10 w-52 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none'
                        >
                          {attachment.dropdownSections.map(
                            (section, sectionIndex) => (
                              <div key={sectionIndex}>
                                {sectionIndex > 0 && (
                                  <MenuSeparator className='my-1 h-px bg-gray-100' />
                                )}
                                <MenuSection>
                                  {section.heading && (
                                    <MenuHeading className='px-4 py-2 text-xs font-semibold text-gray-400'>
                                      {section.heading}
                                    </MenuHeading>
                                  )}
                                  {section.items.map((option, optionIndex) => (
                                    <MenuItem
                                      key={optionIndex}
                                      disabled={option.comingSoon}
                                    >
                                      <button
                                        disabled={option.comingSoon}
                                        className={
                                          option.comingSoon
                                            ? 'flex w-full cursor-not-allowed px-4 py-2 text-left text-sm text-gray-400'
                                            : 'flex w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-50 data-[focus]:text-gray-900'
                                        }
                                        onClick={() =>
                                          !option.comingSoon &&
                                          generatePdf(option)
                                        }
                                      >
                                        {option.comingSoon
                                          ? `${option.label} (coming soon)`
                                          : option.label}
                                      </button>
                                    </MenuItem>
                                  ))}
                                </MenuSection>
                              </div>
                            )
                          )}
                        </MenuItems>
                      </Menu>
                    ))}

                    {canExportPdfBundle && (
                      <li className='relative mr-4 mt-2 list-none'>
                        <button
                          type='button'
                          onClick={onOpenPdfBundle}
                          disabled={isPdfBundleLoading}
                          aria-haspopup='dialog'
                          style={{
                            width: 172,
                            height: 243,
                            background: colors.primaryGreen
                          }}
                          className='relative flex flex-col items-center rounded-xl px-6 hover:opacity-70 disabled:cursor-default disabled:hover:opacity-100'
                        >
                          <div
                            style={{ height: '60%' }}
                            className='flex w-full items-center justify-center'
                          >
                            <img
                              style={{ maxWidth: 100 }}
                              src={PdfBundlePreview}
                              alt=''
                            />
                          </div>

                          <div className='absolute bottom-6 flex flex-col items-center'>
                            <span className='mb-2 text-lg font-medium text-white'>
                              {t('pages.talent.attachments_bundle_title')}
                            </span>
                            <span className='flex min-w-24 items-center justify-center whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-buttonSecondaryBlue shadow-sm ring-1 ring-inset ring-softGrey'>
                              {isPdfBundleLoading ? (
                                <BeatLoader size={6} color='#374151' />
                              ) : (
                                t('pages.talent.attachments_bundle_card_action')
                              )}
                            </span>
                          </div>
                        </button>
                      </li>
                    )}
                  </ul>
                </div>

                {/* Die Zeile steht immer im DOM und wechselt nur ihren Inhalt.
                    Eine Live-Region, die zusammen mit ihrem Text eingehaengt
                    wird, lesen viele Screenreader nicht vor. */}
                <p
                  role='status'
                  aria-live='polite'
                  className='mt-2 text-xs text-gray-500 empty:mt-0'
                >
                  {isPdfBundleLoading
                    ? t('pages.talent.attachments_bundle_hint')
                    : ''}
                </p>
              </div>
            )}
          </div>
        </section>
      </Panel>
      <ProfileFeedbackModal
        open={displayFeedbackModal}
        setOpen={() => setDisplayFeedbackModal(false)}
        context={feedbackModalContext}
      />
    </>
  )
}

SpaceDetailsAssessment.propTypes = {
  space: PropTypes.object.isRequired,
  refetchSpace: PropTypes.func.isRequired,
  career: PropTypes.object,
  spaceActions: PropTypes.object.isRequired,
  canExportPdfBundle: PropTypes.bool,
  isPdfBundleLoading: PropTypes.bool,
  onOpenPdfBundle: PropTypes.func
}

SpaceDetailsAssessment.defaultProps = {
  career: null,
  canExportPdfBundle: false,
  isPdfBundleLoading: false,
  onOpenPdfBundle: () => undefined
}

export default SpaceDetailsAssessment
